import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ulid } from 'ulid';
import {
  computeIndication,
  OtherStateError,
  type IndicationInput,
  type IndicationOutput,
} from '@zg/shared';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eyebrow } from '../components/Eyebrow';
import { Stepper } from '../components/Stepper';
import { Button } from '../components/Button';
import { EmailGate, type EmailGatePayload } from '../components/EmailGate';
import { useIndicationForm, type IndicationDraft } from '../hooks/useIndicationForm';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { StepProjectInfo } from '../components/indication/StepProjectInfo';
import { StepBudget } from '../components/indication/StepBudget';
import { StepTerm } from '../components/indication/StepTerm';
import { StepDeductibles } from '../components/indication/StepDeductibles';
import { postIndication } from '../lib/api';

const STEP_LABELS = ['Project', 'Budget', 'Timing', 'Coverage'];

function draftToInput(draft: IndicationDraft): IndicationInput {
  // The draft is validated by the zod step schemas before we ever arrive here,
  // so every required field is present. We assert non-undefined inline rather
  // than re-checking — invalid state is unreachable on the submit path.
  return {
    namedInsured: draft.namedInsured,
    projectAddress: draft.projectAddress,
    cityStateZip: draft.cityStateZip,
    constructionType: draft.constructionType!,
    numberOfBuildings: draft.numberOfBuildings!,
    numberOfStories: draft.numberOfStories!,
    squareFootage: draft.squareFootage!,
    numberOfUnits: draft.numberOfUnits!,
    state: draft.state!,
    projectDescription: draft.projectDescription,
    hardCosts: draft.hardCosts!,
    softCosts: draft.softCosts!,
    lossOfBusinessIncome: draft.lossOfBusinessIncome,
    startDate: draft.startDate,
    endDate: draft.endDate,
    deductibles: draft.deductibles,
  };
}

export function Indication() {
  const form = useIndicationForm();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();

  const {
    state,
    step,
    next,
    prev,
    goTo,
    blockedOnOtherState,
    draftRestored,
    submit,
    clearDraft,
  } = form;

  const [gateOpen, setGateOpen] = useState(false);
  const [submittingGate, setSubmittingGate] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);

  const isFinal = step === STEP_LABELS.length - 1;
  const nextDisabled = blockedOnOtherState;

  const handlePrimary = () => {
    if (isFinal) {
      const ok = submit();
      if (ok) {
        setGateError(null);
        setGateOpen(true);
      }
    } else {
      next();
    }
  };

  const handleGateSubmit = async ({ email }: EmailGatePayload) => {
    setSubmittingGate(true);
    setGateError(null);
    try {
      const input = draftToInput(state);
      let output: IndicationOutput;
      try {
        output = computeIndication(input);
      } catch (err) {
        if (err instanceof OtherStateError) {
          // Not priced online — route to contact.
          setGateOpen(false);
          navigate('/#contact');
          return;
        }
        throw err;
      }

      const submissionId = ulid();
      const record = {
        submissionId,
        email,
        createdAt: Date.now(),
        input,
        output,
      };
      try {
        window.localStorage.setItem(
          `zg-indication-result-${submissionId}`,
          JSON.stringify(record),
        );
      } catch {
        // Quota/privacy — proceed anyway; the result page will show not-found.
      }

      // Fire-and-forget the API stub; Phase 7 will await + handle errors.
      void postIndication({ submissionId, email, input, output });

      clearDraft();
      setGateOpen(false);
      navigate(`/indication/result/${submissionId}`);
    } catch (err) {
      setGateError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setSubmittingGate(false);
    }
  };

  const transition = reducedMotion ? { duration: 0 } : { duration: 0.1 };

  return (
    <>
      <Navbar />
      <main className="container-site py-16 md:py-20">
        <Eyebrow>BUILDERS_RISK_INDICATION · SELF_SERVE</Eyebrow>
        <h1 className="mt-4 font-display font-medium tracking-[-0.02em] leading-[1.05] text-[clamp(36px,5.4vw,64px)] max-w-3xl">
          Tell us about the project.
        </h1>
        <p className="mt-4 max-w-2xl text-[1rem] leading-relaxed text-ink-muted">
          Four short steps. Your answers stay with us and a Zachary broker follows up within two
          business days.
        </p>

        {draftRestored && (
          <div
            className="mt-6 inline-flex items-center gap-2 bg-panel border border-hairline px-3 py-1.5 rounded-md"
            role="status"
          >
            <span
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full bg-gradient-primary"
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
              &gt; DRAFT_RESTORED
            </span>
          </div>
        )}

        <div className="mt-12">
          <Stepper steps={STEP_LABELS} activeIndex={step} onStepClick={goTo} />
        </div>

        <div className="mt-12 max-w-3xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
            >
              {step === 0 && <StepProjectInfo form={form} />}
              {step === 1 && <StepBudget form={form} />}
              {step === 2 && <StepTerm form={form} />}
              {step === 3 && <StepDeductibles form={form} />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 max-w-3xl">
          <div>
            {step > 0 && (
              <Button variant="ghost" onClick={prev} size="sm">
                Back
              </Button>
            )}
          </div>
          <div>
            {!blockedOnOtherState && (
              <Button
                variant={isFinal ? 'primary' : 'secondary'}
                size="md"
                onClick={handlePrimary}
                disabled={nextDisabled}
              >
                {isFinal ? 'Calculate my indication' : 'Next step'}
              </Button>
            )}
          </div>
        </div>

        {gateError && (
          <p
            className="mt-6 max-w-2xl font-mono text-[11px] uppercase tracking-[0.1em] text-[#b33a3a]"
            role="alert"
          >
            &gt; {gateError}
          </p>
        )}
      </main>
      <Footer />

      <EmailGate
        open={gateOpen}
        onClose={() => {
          if (submittingGate) return;
          setGateOpen(false);
        }}
        onSubmit={handleGateSubmit}
        submitting={submittingGate}
      />
    </>
  );
}
